import Modal from 'react-modal';
import { useEffect, useState } from 'react';
import Button from 'components/Button';
import renderType from './lineTypesRenderer';
import { LINE_TYPE_SELECT_OPTIONS } from './lineTypeOptions';
import renderMarkerType from './makerTypesRenderer';
import { MdClose } from 'react-icons/md';
import { TbArrowsMinimize, TbArrowsMaximize } from 'react-icons/tb';
import Draggable from 'react-draggable';
import { RgbaColorPicker, RgbaColor } from 'react-colorful';
import Slider from 'rc-slider';
import Select, { SingleValue } from 'react-select';
import InputNumber from 'rc-input-number';
import 'rc-slider/assets/index.css';
import 'rc-input-number/assets/index.css';

const lineTypes = LINE_TYPE_SELECT_OPTIONS;

const makerTypes = [
  { value: 'symbols-sls-circle', label: '' },
  { value: 'symbols-sls-cross', label: '' },
  { value: 'symbols-sls-diamond', label: '' },
  { value: 'symbols-sls-square', label: '' },
  { value: 'symbols-sls-triangle', label: '' },
  { value: 'symbols-sls-x', label: '' },
];

interface SymbologyProps {
  isOpen: boolean;
  closeModal: () => void;
  itemKey: string;
  category: string;
  title: string;
  setLayerStyle: (
    key: string,
    color: RgbaColor,
    width: number,
    size: number,
    lineStyle: string,
    markerStyle: string,
    angle: number,
  ) => void;
  minWidth?: number;
  maxWidth?: number;
  minSize?: number;
  maxSize?: number;
  step?: number;
  layerType?: string;
  currentWidth?: number;
  currentSize?: number;
  currentLineStyle?: string;
  currentMarkerStyle?: string;
  currentColor?: RgbaColor;
  currentAngle?: number;
  currentOpacity?: number;
  showDirection: boolean;
  setShowDirection: (value: boolean) => void;
  setLayerLabels?: (key: string, enabled: boolean) => void | Promise<void>;
  setShowDirectionAndWait?: (enabled: boolean) => Promise<void>;
  setLayerOpacity?: (opacity: number) => void;
}

const Symbology: React.FC<SymbologyProps> = ({
  isOpen = false,
  closeModal = () => {},
  itemKey,
  setLayerStyle,
  minWidth = 1,
  maxWidth = 22,
  minSize = 1,
  maxSize = 60,
  step = 1,
  layerType = 'both',
  category,
  title,
  currentWidth = 4,
  currentSize = 22,
  currentLineStyle = 'solid',
  currentMarkerStyle = 'circle',
  currentColor = { r: 48, g: 54, b: 150, a: 1 },
  currentAngle = 0,
  currentOpacity,
  showDirection,
  setShowDirection,
  setLayerLabels,
  setLayerOpacity,
}) => {
  const [isModalMinimized, setModalMinimized] = useState<boolean>(false);
  const [marks, setMarks] = useState<any>({});
  const [pointMarks, setPointMarks] = useState<any>({});
  const [layerWidth, setLayerWidth] = useState<number>(currentWidth);
  const [layerSize, setLayerSize] = useState<number>(currentSize);
  const [angle, setAngle] = useState<number | null>(currentAngle);
  const toggleModalState = () => setModalMinimized((prevState) => !prevState);
  const [color, setColor] = useState<RgbaColor>(currentColor);
  const [opacityPercent, setOpacityPercent] = useState<number>(() =>
    typeof currentOpacity === 'number'
      ? Math.round(Math.min(1, Math.max(0, currentOpacity)) * 100)
      : 100,
  );
  const showOpacity = typeof currentOpacity === 'number';
  const [lineStyle, setLineStyle] = useState<SingleValue<{
    label: string;
    value: string;
  }> | null>({
    label: `symbols-sls-${currentLineStyle}`,
    value: `symbols-sls-${currentLineStyle}`,
  });
  const [markerStyle, setmarkerStyle] = useState<SingleValue<{
    label: string;
    value: string;
  }> | null>({
    label: `symbols-sls-${currentMarkerStyle}`,
    value: `symbols-sls-${currentMarkerStyle}`,
  });

  const [localShowDirection, setLocalShowDirection] =
    useState<boolean>(showDirection);

  const parentKey = (itemKey || '').split('+')[0] ?? '';
  const storageKey = `layerLabels_${itemKey}`;

  const [showLabels, setShowLabels] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? (JSON.parse(stored) as boolean) : false;
    } catch {
      return false;
    }
  });
  const [isApplying, setisApplying] = useState(false);
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      setShowLabels(stored ? (JSON.parse(stored) as boolean) : false);
    } catch {
      setShowLabels(false);
    }
  }, [storageKey]);

  const onSubmit = async () => {
  if (isApplying) return;

  const nextPaint = () => new Promise<void>((r) => requestAnimationFrame(() => r()));

  setisApplying(true);
  await nextPaint();

  try {
    const lineSt = lineStyle?.value?.replace('symbols-sls-', '') || '';
    const markerSt = markerStyle?.value?.replace('symbols-sls-', '') || '';

    setLayerStyle(itemKey, color, layerWidth, layerSize, lineSt, markerSt, angle || 0);
    if (showOpacity && typeof setLayerOpacity === 'function') {
      setLayerOpacity(opacityPercent / 100);
    }
    setShowDirection(localShowDirection);
    
    if (parentKey === 'Assets' && typeof setLayerLabels === 'function') {
      await Promise.resolve(setLayerLabels(itemKey, showLabels));
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(showLabels));
    } catch {}
  } finally {
    setisApplying(false);
  }
};


  useEffect(() => {
    let current = minWidth;
    const newMarks: any = {};
    while (current <= maxWidth) {
      var index = `${current}`;
      newMarks[index as keyof typeof newMarks] = `${current}`;
      current += step * 3;
    }
    setMarks(newMarks);
  }, [minWidth, maxWidth, step]);

  useEffect(() => {
    let current = minWidth;
    const newMarks: any = {};
    while (current <= maxSize) {
      var index = `${current}`;
      newMarks[index as keyof typeof newMarks] = `${current}`;
      current += step * 8;
    }
    setPointMarks(newMarks);
  }, [minSize, maxSize, step]);

  const onSliderChange = (size: number | number[]) => {
    if (Array.isArray(size)) {
      if (size.length > 0) {
        setLayerWidth(size[0]);
      }
    } else {
      setLayerWidth(size);
    }
  };

  const onMarkSliderChange = (size: number | number[]) => {
    if (Array.isArray(size)) {
      if (size.length > 0) {
        setLayerSize(size[0]);
      }
    } else {
      setLayerSize(size);
    }
  };

  const onLineStyleChange = (
    newVal: SingleValue<{
      label: string;
      value: string;
    }>,
  ) => {
    setLineStyle(newVal);
  };

  const onMakerStyleChange = (
    newVal: SingleValue<{
      label: string;
      value: string;
    }>,
  ) => {
    setmarkerStyle(newVal);
  };

  const [windowSize, setWindowSize] = useState({
    innerWidth: 0,
    innerHeight: 0,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const customStyles = {
    content: {
      left: `${(windowSize.innerWidth - 378 - 203) / 2}px`,
      top: `${(windowSize.innerHeight - 425) / 2}px`,
    },
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalShowDirection(!localShowDirection);
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      bodyOpenClassName={null}
      portalClassName="react-modal-container"
      overlayClassName={
        category === 'SurveyDataManager'
          ? 'react-modal-overlay symbology-modal-overlay--sdm'
          : 'react-modal-overlay'
      }
      className="react-modal-wrapper"
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      style={customStyles}
    >
      <Draggable
        enableUserSelectHack={false}
        handle=".modal-symbology"
        cancel=".react-colorful, .custom_select, .rc-slider, .rc-input-number, input, button, .minimal-toggle"
        bounds="body"
      >
        <div
          className={`react-modal symbology-modal${
            category === 'SurveyDataManager' ? ' symbology-modal--sdm' : ''
          }${isModalMinimized ? ' minimized' : ''}`}
        >
          <div className="modal-header modal-symbology">
            <div className="modal-title">Symbology: {title}</div>
            <div className="modal-header-buttons">
              <div className="modal-control" onClick={toggleModalState}>
                {isModalMinimized ? <TbArrowsMaximize /> : <TbArrowsMinimize />}
              </div>
              <div className="modal-close" onClick={() => closeModal()}>
                <MdClose />
              </div>
            </div>
          </div>
          <div className="modal-content">
            {category === 'Network' && (
              <div
                className="form-group"
                style={{ marginTop: 8, marginBottom: 8 }}
              >
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <input
                    type="checkbox"
                    checked={localShowDirection}
                    onChange={handleCheckboxChange}
                  />
                  Show Direction
                </label>
              </div>
            )}
            <RgbaColorPicker color={color} onChange={setColor} />
            {showOpacity && (
              <div style={{ marginTop: '16px' }}>
                <strong>Opacity ({opacityPercent}%)</strong>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={opacityPercent}
                  onChange={(value) => {
                    const resolved = Array.isArray(value) ? value[0] : value;
                    setOpacityPercent(resolved);
                  }}
                />
              </div>
            )}
            {(layerType === 'simple-line' || layerType === 'both') && (
              <>
                <div style={{ marginTop: '16px', paddingBottom: '20px' }}>
                  <strong>Line</strong>
                  <Slider
                    min={minWidth}
                    max={maxWidth}
                    step={step}
                    value={layerWidth}
                    marks={marks}
                    onChange={onSliderChange}
                  />
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Select
                    options={lineTypes}
                    onChange={onLineStyleChange}
                    value={lineStyle}
                    className={'custom_select'}
                    menuPlacement="top"
                    classNamePrefix="custom_select"
                    formatOptionLabel={(lineType) => (
                      <div>{renderType(lineType.value, color)}</div>
                    )}
                  />
                </div>
              </>
            )}
            {(layerType === 'simple-marker' || layerType === 'both') && (
              <>
                <div style={{ marginTop: '16px' }}>
                  <strong>Point</strong>
                  <Slider
                    min={minSize}
                    max={maxSize}
                    step={step}
                    value={layerSize}
                    marks={pointMarks}
                    onChange={onMarkSliderChange}
                  />
                </div>
                <div style={{ marginTop: '16px' }}>
                  <Select
                    options={makerTypes}
                    value={markerStyle}
                    className={'custom_select'}
                    classNamePrefix="custom_select"
                    menuPlacement="top"
                    onChange={onMakerStyleChange}
                    formatOptionLabel={(makerType) => (
                      <div>{renderMarkerType(makerType.value, color)}</div>
                    )}
                  />
                </div>
                <div style={{ marginTop: '16px' }}>
                  <strong>Angle</strong>
                  <InputNumber
                    min={0}
                    max={360}
                    step={1}
                    value={angle}
                    onChange={setAngle}
                  />
                </div>
              </>
            )}
          </div>
          {parentKey === 'Assets' && (
              <div className="minimal-labels-ui">
                <div className="minimal-switch">
                  <div className="minimal-switch-label">Display Labels on {title}</div>
                  <div 
                    className={`minimal-toggle ${showLabels ? 'active' : ''}`}
                    onClick={() => setShowLabels((prev) => !prev)}
                  >
                    <div className="minimal-toggle-inner"></div>
                  </div>
                </div>
                </div>
            )}
          <div className="modal-footer">
            <Button
              onClick={onSubmit}
              color="primary"
              full
              isButton
              disabled={isApplying}
            >
              {isApplying ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  Applying...
                </span>
              ) : (
                'Apply'
              )}
            </Button>
          </div>
        </div>
      </Draggable>
    </Modal>
  );
};

export default Symbology;
